"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import type { TaskFilters, FilterRanges } from "@/types/filters";
import { getFilterRanges } from "@/app/queries";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { SlidersHorizontal, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
} from "nuqs";

interface TaskFiltersProps {
  filters: TaskFilters;
  onFiltersChange: (filters: TaskFilters) => void;
}

export function TaskFiltersComponent({
  filters,
  onFiltersChange,
}: TaskFiltersProps) {
  const [ranges, setRanges] = useState<FilterRanges | null>(null);
  const [loading, setLoading] = useState(true);
  const [localFilters, setLocalFilters] = useState<TaskFilters>(filters);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const isUpdatingFromProps = useRef(false);

  const [dataset, setDataset] = useQueryState("dataset", parseAsString);
  const [colorCount, setColorCount] = useQueryState(
    "colorCount",
    parseAsArrayOf(parseAsInteger, ","),
  );
  const [trainCount, setTrainCount] = useQueryState(
    "trainCount",
    parseAsArrayOf(parseAsInteger, ","),
  );
  const [testCount, setTestCount] = useQueryState(
    "testCount",
    parseAsArrayOf(parseAsInteger, ","),
  );
  const [avgSize, setAvgSize] = useQueryState(
    "avgSize",
    parseAsArrayOf(parseAsInteger, ","),
  );

  useEffect(() => {
    getFilterRanges().then((r) => {
      setRanges(r);
      setLoading(false);
    });
  }, []);

  // Update local filters when external filters change
  useEffect(() => {
    isUpdatingFromProps.current = true;
    setLocalFilters(filters);
    if (filters.dataset !== dataset) {
      setDataset(filters.dataset || null);
    }
    if (JSON.stringify(filters.colorCount) !== JSON.stringify(colorCount)) {
      setColorCount(filters.colorCount || null);
    }
    if (JSON.stringify(filters.trainCount) !== JSON.stringify(trainCount)) {
      setTrainCount(filters.trainCount || null);
    }
    if (JSON.stringify(filters.testCount) !== JSON.stringify(testCount)) {
      setTestCount(filters.testCount || null);
    }
    if (JSON.stringify(filters.avgSize) !== JSON.stringify(avgSize)) {
      setAvgSize(filters.avgSize || null);
    }
    setTimeout(() => {
      isUpdatingFromProps.current = false;
    }, 0);
  }, [
    filters,
    dataset,
    colorCount,
    trainCount,
    testCount,
    avgSize,
    setDataset,
    setColorCount,
    setTrainCount,
    setTestCount,
    setAvgSize,
  ]);

  useEffect(() => {
    if (isUpdatingFromProps.current) return;
    
    onFiltersChange({
      dataset: dataset ?? undefined,
      colorCount: colorCount as [number, number] | undefined,
      trainCount: trainCount as [number, number] | undefined,
      testCount: testCount as [number, number] | undefined,
      avgSize: avgSize as [number, number] | undefined,
    });
  }, [
    dataset,
    colorCount,
    trainCount,
    testCount,
    avgSize,
    onFiltersChange,
  ]);
  // Debounced filter update function
  const debouncedUpdateFilters = useCallback(
    (newFilters: TaskFilters) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        onFiltersChange(newFilters);
      }, 300); // 300ms debounce delay
    },
    [onFiltersChange],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  if (loading || !ranges) {
    return (
      <div className="p-4 space-y-4">
        <div className="h-4 bg-primary/10 animate-pulse rounded" />
        <div className="h-4 bg-primary/10 animate-pulse rounded" />
        <div className="h-4 bg-primary/10 animate-pulse rounded" />
      </div>
    );
  }

  const updateFilter = (key: keyof TaskFilters, value: any) => {
    const newFilters = {
      ...localFilters,
      [key]: value,
    };
    setLocalFilters(newFilters);

    // For dataset changes, update immediately (no debounce)
    if (key === "dataset") {
      onFiltersChange(newFilters);
    } else {
      // For slider changes, use debounced update
      debouncedUpdateFilters(newFilters);
    }
  };

  const resetSliderFilters = () => {
    const newFilters = {
      ...localFilters,
      colorCount: undefined,
      trainCount: undefined,
      testCount: undefined,
      avgSize: undefined,
    };
    setLocalFilters(newFilters);

    // Clear any pending debounced updates
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Apply reset immediately
    onFiltersChange(newFilters);
  };

  const hasActiveSliderFilters =
    (localFilters.colorCount ? 1 : 0) +
    (localFilters.trainCount ? 1 : 0) +
    (localFilters.testCount ? 1 : 0) +
    (localFilters.avgSize ? 1 : 0);

  return (
    <div className="p-4 border-b">
      {/* Filters Row */}
      <div className="grid grid-cols-2 gap-3">
        {/* Dataset Filter */}
        <div className="space-y-2">
          <Label className="text-xs font-mono text-primary/60">Dataset</Label>
          <Select
            value={localFilters.dataset || "all"}
            onValueChange={(value) =>
              updateFilter("dataset", value === "all" ? undefined : value)
            }
          >
            <SelectTrigger
              className="text-xs font-mono cursor-pointer h-8"
              size="sm"
            >
              <SelectValue placeholder="All datasets" />
            </SelectTrigger>
            <SelectContent className="max-h-[400px] overflow-y-auto">
              <SelectItem value="all">All datasets</SelectItem>
              {ranges.datasets.map((dataset) => (
                <SelectItem key={dataset} value={dataset}>
                  {dataset}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Range Filters Dropdown */}
        <div className="space-y-2">
          <Label className="text-xs font-mono text-primary/60">
            Range Filters
          </Label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={`h-8 text-xs font-mono w-full justify-between cursor-pointer ${
                  hasActiveSliderFilters ? "border-primary" : ""
                }`}
              >
                <div
                  className={cn(
                    "flex items-center gap-2",
                    hasActiveSliderFilters && "font-bold",
                  )}
                >
                  <SlidersHorizontal
                    className="h-3 w-3"
                    strokeWidth={hasActiveSliderFilters ? 2 : 1}
                  />
                  Filters{" "}
                  {hasActiveSliderFilters ? `(${hasActiveSliderFilters})` : ""}
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-80 p-4" align="start">
              <div className="space-y-4">
                {/* Reset Button */}
                <div className="flex justify-between items-center">
                  <Label className="text-sm font-mono text-primary">
                    Range Filters
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetSliderFilters}
                    className="h-6 px-2 text-xs"
                    disabled={!hasActiveSliderFilters}
                  >
                    <RotateCcw className="h-3 w-3 mr-1" />
                    Reset
                  </Button>
                </div>

                <Separator />

                {/* Color Count Filter */}
                <div className="space-y-2">
                  <Label className="text-xs font-mono text-primary/60">
                    Colors:{" "}
                    {localFilters.colorCount?.[0] ?? ranges.colorCount[0]} -{" "}
                    {localFilters.colorCount?.[1] ?? ranges.colorCount[1]}
                  </Label>
                  <Slider
                    value={localFilters.colorCount || ranges.colorCount}
                    onValueChange={(value) =>
                      updateFilter("colorCount", value as [number, number])
                    }
                    min={ranges.colorCount[0]}
                    max={ranges.colorCount[1]}
                    step={1}
                    className="w-full"
                  />
                </div>

                {/* Train Count Filter */}
                <div className="space-y-2">
                  <Label className="text-xs font-mono text-primary/60">
                    Train:{" "}
                    {localFilters.trainCount?.[0] ?? ranges.trainCount[0]} -{" "}
                    {localFilters.trainCount?.[1] ?? ranges.trainCount[1]}
                  </Label>
                  <Slider
                    value={localFilters.trainCount || ranges.trainCount}
                    onValueChange={(value) =>
                      updateFilter("trainCount", value as [number, number])
                    }
                    min={ranges.trainCount[0]}
                    max={ranges.trainCount[1]}
                    step={1}
                    className="w-full"
                  />
                </div>

                {/* Test Count Filter */}
                <div className="space-y-2">
                  <Label className="text-xs font-mono text-primary/60">
                    Test: {localFilters.testCount?.[0] ?? ranges.testCount[0]} -{" "}
                    {localFilters.testCount?.[1] ?? ranges.testCount[1]}
                  </Label>
                  <Slider
                    value={localFilters.testCount || ranges.testCount}
                    onValueChange={(value) =>
                      updateFilter("testCount", value as [number, number])
                    }
                    min={ranges.testCount[0]}
                    max={ranges.testCount[1]}
                    step={1}
                    className="w-full"
                  />
                </div>

                {/* Average Size Filter */}
                <div className="space-y-2">
                  <Label className="text-xs font-mono text-primary/60">
                    Avg Size:{" "}
                    {(localFilters.avgSize?.[0] ?? ranges.avgSize[0]).toFixed(
                      1,
                    )}{" "}
                    -{" "}
                    {(localFilters.avgSize?.[1] ?? ranges.avgSize[1]).toFixed(
                      1,
                    )}
                  </Label>
                  <Slider
                    value={localFilters.avgSize || ranges.avgSize}
                    onValueChange={(value) =>
                      updateFilter("avgSize", value as [number, number])
                    }
                    min={ranges.avgSize[0]}
                    max={ranges.avgSize[1]}
                    step={0.1}
                    className="w-full"
                  />
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
